import React, { useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { DDSIconDefinition, Flex, InlineButton, Table, Text, useTableState } from '@driftt/dds'
import { faArrowPointer } from '@driftt/dds.icons/light/faArrowPointer'
import { faCalendarCheck } from '@driftt/dds.icons/light/faCalendarCheck'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faPaperPlane } from '@driftt/dds.icons/light/faPaperPlane'
import { faReply } from '@driftt/dds.icons/light/faReply'

import {
  AutomationAccountSummary,
  fetchAllAccountsEnrolled,
  fetchUserAccountsEnrolled
} from 'api/EmailAutomationAPI'
import { NavigationWrapper } from 'components/AccountEngagementTable'
import AccountFitTooltip from 'components/AccountFitTooltip'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import EngagementScoreSimplified from 'components/EngagementScoreSimplified'
import { CompanyData } from 'modules/accountActivity/domain'

import { ContactsPopup } from './ContactsPopup'
import { IconWithCount } from './IconWithCount'
import { DateRange, getAccountDisplayName } from './utils'

interface AccountsEnrolledTableProps {
  dateRange: DateRange
  selectedUser: string
  openModal: (eventType: string, accountId: string, accountName: string) => void
  searchQuery: string
}
interface ModalButtonProps {
  icon: DDSIconDefinition
  count: number
  openModal: () => void
}
const ModalButton = ({ icon, count, openModal }: ModalButtonProps) =>
  count ? (
    <InlineButton onClick={openModal} disabled={!count}>
      <IconWithCount icon={icon} count={count} isButton={true} />
    </InlineButton>
  ) : (
    <IconWithCount icon={icon} count={count} isButton={true} />
  )

const AccountInfo = ({
  accountId,
  companyData
}: {
  accountId: string
  companyData: CompanyData
}) => {
  return (
    <NavigationWrapper accountId={accountId} accountName={companyData?.name}>
      <Flex alignItems="center">
        <AccountActivityAccountAvatar imageUrl={companyData?.logo} name={companyData?.name} />
        <Text
          variant="bodyBold"
          css={{
            maxWidth: '130px'
          }}
          truncate>
          {companyData?.name || companyData?.domain || 'Unnamed account'}
        </Text>
      </Flex>
    </NavigationWrapper>
  )
}

const AccountsEnrolledTable = ({
  dateRange,
  selectedUser,
  openModal,
  searchQuery
}: AccountsEnrolledTableProps) => {
  const { tableState } = useTableState<AutomationAccountSummary>({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 5
    }
  })

  const { data, isLoading } = useQuery<AutomationAccountSummary[]>({
    queryKey: ['getAutomatedAccounts', dateRange.startTime, dateRange.endTime, selectedUser],
    queryFn: () => {
      return selectedUser === 'all'
        ? fetchAllAccountsEnrolled(dateRange.startTime, dateRange.endTime).then(res => res.data)
        : fetchUserAccountsEnrolled(selectedUser, dateRange.startTime, dateRange.endTime).then(
            res => res.data
          )
    }
  })

  const [filteredData, setFilteredData] = useState<AutomationAccountSummary[]>([])

  useEffect(() => {
    setFilteredData(
      data
        ?.filter(account =>
          account.companyData?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map(row => ({
          ...row,
          companyName: row.companyData?.name || row.companyData?.domain || 'Unnamed account'
        })) ?? []
    )
  }, [data, searchQuery])

  return (
    <Flex direction="column" fullHeight fullWidth>
      <Table<AutomationAccountSummary>
        rows={filteredData ?? []}
        isLoading={isLoading}
        pagination={{
          rowTotal: filteredData?.length ?? 0,
          defaultPagination: tableState.pagination,
          enableClientSidePagination: true
        }}
        columns={[
          {
            value: 'companyName',
            header: 'Account',
            cell: ({ accountId, companyData }) => (
              <AccountInfo accountId={accountId} companyData={companyData} />
            )
          },
          {
            value: 'accountFitScore',
            header: 'Account fit',
            size: 96,
            cell: ({ accountFitScore, featureWeights, companyData }) => (
              <AccountFitTooltip
                accountFitScore={accountFitScore}
                companyData={companyData}
                scoreBreakdown={featureWeights}
              />
            )
          },
          {
            value: 'normalizedEngagementScore',
            header: 'Engagement',
            size: 96,
            cell: ({ normalizedEngagementScore }) => (
              <EngagementScoreSimplified score={normalizedEngagementScore} />
            )
          },
          {
            value: 'contactsEnrolled',
            header: 'Enrolled',
            size: 96,
            cell: ({ contactsEnrolled, accountId }) => (
              <ContactsPopup
                numContacts={contactsEnrolled}
                accountId={accountId}
                dateRange={dateRange}
              />
            )
          },
          {
            value: 'scheduledCount',
            header: 'Scheduled',
            size: 96,
            cell: ({ scheduledCount, accountId, companyData }) => (
              <ModalButton
                icon={faCalendarCheck}
                count={scheduledCount}
                openModal={() =>
                  scheduledCount &&
                  openModal('scheduled', accountId, getAccountDisplayName(companyData))
                }
              />
            )
          },
          {
            value: 'sentCount',
            header: 'Sent',
            size: 96,
            cell: ({ sentCount, accountId, companyData }) => (
              <ModalButton
                icon={faPaperPlane}
                count={sentCount}
                openModal={() =>
                  sentCount && openModal('send', accountId, getAccountDisplayName(companyData))
                }
              />
            )
          },
          {
            value: 'openCount',
            header: 'Opened',
            size: 96,
            cell: ({ openCount, accountId, companyData }) => (
              <ModalButton
                icon={faEye}
                count={openCount}
                openModal={() =>
                  openCount && openModal('open', accountId, getAccountDisplayName(companyData))
                }
              />
            )
          },
          {
            value: 'clickCount',
            header: 'Clicked',
            size: 96,
            cell: ({ clickCount, accountId, companyData }) => (
              <ModalButton
                icon={faArrowPointer}
                count={clickCount}
                openModal={() =>
                  clickCount && openModal('click', accountId, getAccountDisplayName(companyData))
                }
              />
            )
          },
          {
            value: 'replyCount',
            header: 'Replied',
            size: 96,
            cell: ({ replyCount, accountId, companyData }) => (
              <ModalButton
                icon={faReply}
                count={replyCount}
                openModal={() =>
                  replyCount && openModal('reply', accountId, getAccountDisplayName(companyData))
                }
              />
            )
          }
        ]}
      />
    </Flex>
  )
}

export default AccountsEnrolledTable
